/**
 * WorkspaceSecrets - Admin UI for managing per-workspace API keys.
 * Lives inside Settings > Workspaces > [workspace] drill-in.
 *
 * Split: cloud providers / custom endpoint / extension secrets.
 * Chrome: SettingsSection (Basecamp Project folders SoT).
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import {
  Key,
  Eye,
  EyeOff,
  Trash2,
  TestTube,
  CheckCircle2,
  AlertCircle,
  Loader2,
  RefreshCw,
  Puzzle,
  Server,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import {
  SettingsSection,
  SettingsSectionRow,
} from '@/components/features/settings/settings-section'
import { fetchWithAuth } from '@/lib/api'
import { testIds } from '@/lib/test-ids'
import { toast } from 'sonner'
import { QUIET_ICON_BUTTON_DESTRUCTIVE_CLASS } from '@/lib/menu-chrome'
import { cn } from '@/lib/utils'

interface SecretKeyInfo {
  key_name: string
  is_set: boolean
  rotated_at: string | null
  created_at: string | null
  has_platform_default: boolean
}

interface WorkspaceSecretsProps {
  orgId: string
  isAdmin: boolean
}

const CLOUD_PROVIDER_KEYS = [
  'ANTHROPIC_API_KEY',
  'OPENAI_API_KEY',
  'GROQ_API_KEY',
  'XAI_API_KEY',
  'BRAVE_SEARCH_API_KEY',
] as const

const CUSTOM_ENDPOINT_KEYS = [
  'LLM_BASE_URL',
  'LLM_ENDPOINT_API_KEY',
  'LLM_ENDPOINT_MODEL',
  'LOCALAI_API_KEY',
  'OLLAMA_API_KEY',
] as const

const LLM_KEY_NAMES = new Set<string>([...CLOUD_PROVIDER_KEYS, ...CUSTOM_ENDPOINT_KEYS])

function pickKeysInOrder(keys: SecretKeyInfo[], order: readonly string[]): SecretKeyInfo[] {
  const byName = new Map(keys.map((key) => [key.key_name, key]))
  return order.flatMap((name) => {
    const row = byName.get(name)
    return row ? [row] : []
  })
}

const KEY_LABELS: Record<string, { label: string; description: string; provider: string }> = {
  ANTHROPIC_API_KEY: {
    label: 'Anthropic API Key',
    description: 'Powers Claude models (Sonnet, Haiku, Opus)',
    provider: 'Anthropic',
  },
  OPENAI_API_KEY: {
    label: 'OpenAI API Key',
    description: 'Powers GPT models when chat is enabled, plus Whisper transcription',
    provider: 'OpenAI',
  },
  GROQ_API_KEY: {
    label: 'Groq API Key',
    description: 'Fast inference for Llama, Qwen, and other open models',
    provider: 'Groq',
  },
  XAI_API_KEY: {
    label: 'xAI API Key',
    description: 'Powers Grok models (grok-4.6) via api.x.ai',
    provider: 'xAI',
  },
  BRAVE_SEARCH_API_KEY: {
    label: 'Brave Search API Key',
    description: 'Web search tool in chat',
    provider: 'Brave',
  },
  OLLAMA_API_KEY: {
    label: 'Ollama API Key',
    description: 'Bearer token for remote / reverse-proxied Ollama',
    provider: 'Ollama',
  },
  LOCALAI_API_KEY: {
    label: 'LocalAI / OpenAI-compatible API key',
    description: 'Bearer token for Open WebUI, vLLM, LiteLLM, LocalAI',
    provider: 'LocalAI',
  },
  LLM_BASE_URL: {
    label: 'Custom inference base URL',
    description: 'OpenAI-compatible base (e.g. https://ai.gnu.fun/api/v1). Chat uses this when Custom endpoint is enabled.',
    provider: 'BYO endpoint',
  },
  LLM_ENDPOINT_API_KEY: {
    label: 'Custom inference API key',
    description: 'Key for LLM_BASE_URL. Same as LOCALAI_API_KEY if you only set one.',
    provider: 'BYO endpoint',
  },
  LLM_ENDPOINT_MODEL: {
    label: 'Custom inference model id',
    description: 'Model name as the endpoint lists it (e.g. qwen2.5:32b)',
    provider: 'BYO endpoint',
  },
  CURSOR_API_KEY: {
    label: 'Cursor API Key',
    description: 'Cloud agent dispatch - get your key at cursor.com/dashboard/integrations',
    provider: 'Cursor',
  },
}

function isLlmToolKey(keyName: string): boolean {
  return LLM_KEY_NAMES.has(keyName)
}

export function WorkspaceSecrets({ orgId, isAdmin }: WorkspaceSecretsProps) {
  const [keys, setKeys] = useState<SecretKeyInfo[]>([])
  const [loading, setLoading] = useState(true)
  const [editingKey, setEditingKey] = useState<string | null>(null)
  const [inputValue, setInputValue] = useState('')
  const [showValue, setShowValue] = useState(false)
  const [testing, setTesting] = useState<string | null>(null)
  const [testResults, setTestResults] = useState<
    Record<string, { success: boolean; message?: string; error?: string }>
  >({})
  const [saving, setSaving] = useState(false)

  const fetchKeys = useCallback(async () => {
    try {
      const res = await fetchWithAuth(`/api/organizations/${orgId}/secrets`)
      const data = await res.json()
      if (data.success) setKeys(data.keys)
    } catch {
      toast.error('Failed to load API keys')
    } finally {
      setLoading(false)
    }
  }, [orgId])

  useEffect(() => {
    fetchKeys()
  }, [fetchKeys])

  const cloudKeys = useMemo(
    () => pickKeysInOrder(keys, CLOUD_PROVIDER_KEYS),
    [keys],
  )
  const customKeys = useMemo(
    () => pickKeysInOrder(keys, CUSTOM_ENDPOINT_KEYS),
    [keys],
  )
  const extensionKeys = useMemo(() => keys.filter((k) => !isLlmToolKey(k.key_name)), [keys])

  const handleSave = async (keyName: string) => {
    if (!inputValue.trim()) return
    setSaving(true)
    try {
      const res = await fetchWithAuth(`/api/organizations/${orgId}/secrets/${keyName}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: inputValue }),
      })
      const data = await res.json()
      if (data.success) {
        toast.success(`${KEY_LABELS[keyName]?.label || keyName} saved`)
        setEditingKey(null)
        setInputValue('')
        setShowValue(false)
        fetchKeys()
      } else {
        toast.error(data.error || 'Failed to save key')
      }
    } catch {
      toast.error('Failed to save key')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (keyName: string) => {
    try {
      const res = await fetchWithAuth(`/api/organizations/${orgId}/secrets/${keyName}`, {
        method: 'DELETE',
      })
      const data = await res.json()
      if (data.success) {
        toast.success(`${KEY_LABELS[keyName]?.label || keyName} removed`)
        fetchKeys()
        setTestResults((prev) => {
          const next = { ...prev }
          delete next[keyName]
          return next
        })
      } else {
        toast.error(data.error || 'Failed to remove key')
      }
    } catch {
      toast.error('Failed to remove key')
    }
  }

  const handleTest = async (keyName: string) => {
    setTesting(keyName)
    try {
      const res = await fetchWithAuth(`/api/organizations/${orgId}/secrets/${keyName}/test`, {
        method: 'POST',
      })
      const data = await res.json()
      setTestResults((prev) => ({ ...prev, [keyName]: data }))
      if (data.success) {
        toast.success(data.message || 'Key valid')
      } else {
        toast.error(data.error || 'Key test failed')
      }
    } catch {
      toast.error('Test request failed')
    } finally {
      setTesting(null)
    }
  }

  const renderKeyRow = (key: SecretKeyInfo) => {
    const meta = KEY_LABELS[key.key_name] || {
      label: key.key_name,
      description: '',
      provider: '',
    }
    const isEditing = editingKey === key.key_name
    const testResult = testResults[key.key_name]

    return (
      <SettingsSectionRow key={key.key_name} className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2 min-w-0">
            <span className="font-medium text-sm">{meta.label}</span>
            {key.is_set ? (
              <Badge
                variant="outline"
                className="text-green-600 border-green-500/40 bg-green-500/10 text-xs"
              >
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Configured
              </Badge>
            ) : key.has_platform_default ? (
              <Badge
                variant="outline"
                className="text-blue-600 border-blue-500/40 bg-blue-500/10 text-xs"
              >
                Platform default
              </Badge>
            ) : (
              <Badge variant="outline" className="text-muted-foreground border-border text-xs">
                <AlertCircle className="h-3 w-3 mr-1" />
                Not set
              </Badge>
            )}
            {testResult && (
              <Badge
                variant="outline"
                className={
                  testResult.success
                    ? 'text-green-600 border-green-500/40 bg-green-500/10 text-xs'
                    : 'text-red-600 border-red-500/40 bg-red-500/10 text-xs'
                }
              >
                {testResult.success ? 'Valid' : 'Invalid'}
              </Badge>
            )}
          </div>

          {isAdmin && (
            <div className="flex items-center gap-1 shrink-0">
              {(key.is_set || key.has_platform_default) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  disabled={testing === key.key_name}
                  onClick={() => handleTest(key.key_name)}
                >
                  {testing === key.key_name ? (
                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  ) : (
                    <TestTube className="h-3 w-3 mr-1" />
                  )}
                  Test
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => {
                  setEditingKey(isEditing ? null : key.key_name)
                  setInputValue('')
                  setShowValue(false)
                }}
              >
                {key.is_set ? 'Rotate' : 'Set'}
              </Button>
              {key.is_set && (
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn('h-7 text-xs', QUIET_ICON_BUTTON_DESTRUCTIVE_CLASS)}
                  onClick={() => handleDelete(key.key_name)}
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              )}
            </div>
          )}
        </div>

        {meta.description ? (
          <p className="text-xs text-muted-foreground">{meta.description}</p>
        ) : null}

        {key.rotated_at ? (
          <p className="text-xs text-muted-foreground">
            Last updated {new Date(key.rotated_at).toLocaleDateString()}
          </p>
        ) : null}

        {isEditing && isAdmin ? (
          <div className="mt-2 space-y-2">
            <div className="relative">
              <Input
                type={showValue ? 'text' : 'password'}
                placeholder={`Enter ${meta.label}`}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                className="pr-10 text-sm font-mono"
                onKeyDown={(e) => e.key === 'Enter' && handleSave(key.key_name)}
                autoFocus
              />
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7"
                onClick={() => setShowValue(!showValue)}
              >
                {showValue ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
              </Button>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                className="h-7 text-xs"
                disabled={!inputValue.trim() || saving}
                onClick={() => handleSave(key.key_name)}
              >
                {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                Save
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={() => {
                  setEditingKey(null)
                  setInputValue('')
                  setShowValue(false)
                }}
              >
                Cancel
              </Button>
            </div>
          </div>
        ) : null}
      </SettingsSectionRow>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-4 text-muted-foreground text-sm">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading API keys...
      </div>
    )
  }

  const refreshAction = (
    <Button variant="ghost" size="icon" onClick={fetchKeys} title="Refresh">
      <RefreshCw className="h-3.5 w-3.5" />
    </Button>
  )

  return (
    <div className="space-y-5">
      <SettingsSection
        data-testid={testIds.workspaceSecrets.cloudSection}
        icon={<Key />}
        title="Cloud providers"
        description={
          isAdmin
            ? "Anthropic, OpenAI, Groq, xAI, and search. Encrypted at rest. Platform default uses Maater's key."
            : 'Your workspace admin has configured these API keys.'
        }
        actions={refreshAction}
      >
        {cloudKeys.length === 0 ? (
          <p className="text-sm text-muted-foreground">No provider keys to show. Try refreshing.</p>
        ) : (
          <div className="space-y-3">{cloudKeys.map(renderKeyRow)}</div>
        )}
      </SettingsSection>

      {customKeys.length > 0 ? (
        <SettingsSection
          data-testid={testIds.workspaceSecrets.customSection}
          icon={<Server />}
          title="Custom endpoint"
          description="Point this workspace at Open WebUI, vLLM, LocalAI, or remote Ollama. Enable Custom endpoint under AI & LLM to use it in chat."
        >
          <div className="space-y-3">{customKeys.map(renderKeyRow)}</div>
        </SettingsSection>
      ) : null}

      {extensionKeys.length > 0 ? (
        <SettingsSection
          data-testid={testIds.workspaceSecrets.extensionSection}
          icon={<Puzzle />}
          title="Extension secrets"
          description={
            <>
              Keys for workspace extensions (for example Cursor cloud agents). Prefer Connect on
              each extension when it offers OAuth; paste keys here only when required.
            </>
          }
        >
          <div className="space-y-3">{extensionKeys.map(renderKeyRow)}</div>
        </SettingsSection>
      ) : null}
    </div>
  )
}
